<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { blogPageUrl } from '~/constants/blog'

const props = defineProps<{
  page: number
  pageCount: number
  basePath: string
}>()

const { t } = useI18n()

const pageUrl = (target: number) => blogPageUrl(props.basePath, target)

/**
 * Every page is rendered as a link, not a windowed subset.
 *
 * At this scale that keeps every listing page one hop from every other, which is
 * the cheapest possible crawl graph. Revisit only if the page count gets large
 * enough that the nav becomes unwieldy for readers.
 */
const pages = computed(() => Array.from({ length: props.pageCount }, (_, i) => i + 1))
</script>

<!--
  Real <a href> pagination. This replaced a "Load more" button that incremented a
  component ref: page 2 and beyond had no URL at all, so everything past the first
  page was reachable only from the sitemap.

  Visuals are the template's round 50px buttons (`.pagination` / `.page-item`),
  the current page filled lilac.
-->
<template>
  <nav v-if="pageCount > 1" :aria-label="t('blog.pagination')" class="mt-10 md:mt-[3.75rem] lg:mt-20">
    <ol class="pagination">
      <li v-if="page > 1">
        <LocaleLink :to="pageUrl(page - 1)" rel="prev" :aria-label="t('blog.previousPage')" class="page-item">
          <i class="bi bi-arrow-left" aria-hidden="true" />
        </LocaleLink>
      </li>
      <li v-for="target in pages" :key="target">
        <LocaleLink
          :to="pageUrl(target)"
          :aria-current="target === page ? 'page' : undefined"
          class="page-item"
          :class="{ active: target === page }"
        >
          {{ target }}
        </LocaleLink>
      </li>
      <li v-if="page < pageCount">
        <LocaleLink :to="pageUrl(page + 1)" rel="next" :aria-label="t('blog.nextPage')" class="page-item">
          <i class="bi bi-arrow-right" aria-hidden="true" />
        </LocaleLink>
      </li>
    </ol>
  </nav>
</template>
